import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, untrack } from "solid-js";
import { useWs } from "./ws";
import {
  captureBugReportContext,
  captureBugReportDomSnapshot,
  captureBugReportEvidenceDelta,
  captureBugReportVisualOneShot,
  captureBugReportVisualRegion,
  clearBugReportElementHighlight,
  editBugReportVisualCapture,
  getBugReportBuffers,
  highlightBugReportElement,
  installBugReportCapture,
  sanitizeText,
  selectBugReportDomAnnotations,
  selectBugReportElement,
  startBugReportActionRecording,
  type BugReportActionSnapshot,
  type BugReportActionRecorder,
  type BugReportContext,
  type BugReportDraftSnapshot,
  type BugReportEvidenceItem,
  type BugReportEvidenceCaptureWindow,
  type DomAnnotationSnapshot,
  type DomNodeSnapshot,
  type ElementTargetSnapshot,
  type VisualCaptureSnapshot,
} from "./bug-report-capture";
import { isNativeApp } from "./native";

type Category = "bug" | "ui" | "network" | "performance" | "security" | "other";
type Severity = "low" | "medium" | "high" | "critical";
type RecordingKind = "action" | "telemetry";
type EvidenceEventKind = "actions" | "console" | "network" | "wsMessages";
type EvidenceConsoleEvent = BugReportEvidenceItem["contextDelta"]["console"][number];
type EvidenceNetworkEvent = BugReportEvidenceItem["contextDelta"]["network"][number];
type EvidenceWsEvent = BugReportEvidenceItem["contextDelta"]["wsMessages"][number];
type EventPreview = { title: string; meta: string; body: string };
type EvidenceEventCarryover = {
  console: EvidenceConsoleEvent[];
  network: EvidenceNetworkEvent[];
  wsMessages: EvidenceWsEvent[];
};

interface ConfigResponse {
  ok: boolean;
  enabled: boolean;
  richCaptureEnabled: boolean;
}

interface SubmitResponse {
  ok: boolean;
  report?: { id: string; status: string };
  error?: string;
}

interface SubmitRequestBody {
  category: Category;
  severity: Severity;
  userComment: string;
  errorMessage: string;
  sessionCode: string | null;
  hostId: string | null;
  route: string;
  panelId: string | null;
  context: BugReportContext;
}

interface PersistedBugReportDraftState {
  v: 1;
  savedAt: number;
  open: boolean;
  fullscreen: boolean;
  comment: string;
  errorMessage: string;
  category: Category;
  severity: Severity;
  includeDom: boolean;
  draft: BugReportDraftSnapshot | null;
  evidenceItems: BugReportEvidenceItem[];
  lastEvidenceAt: number;
  lastWsEvidence: Record<string, unknown> | null;
  activeRecording: { kind: RecordingKind; startedAt: number } | null;
  recordingEvents?: EvidenceEventCarryover | null;
}

const MAX_EVIDENCE_ITEMS = 10;
const MAX_SUBMIT_PAYLOAD_BYTES = 1_800_000;
const MAX_PERSISTED_DRAFT_BYTES = 4_500_000;
const MAX_PERSISTED_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_PERSISTED_RECORDING_EVENTS = 80;
const COMPACT_GLOBAL_EVENTS = 30;
const COMPACT_ITEM_EVENTS = 20;
const BUG_REPORT_DRAFT_STORAGE_KEY = "remotews.bugReportDraft.v1";
const CATEGORIES: Category[] = ["bug", "ui", "network", "performance", "security", "other"];
const SEVERITIES: Severity[] = ["low", "medium", "high", "critical"];

export function BugReportWidget() {
  const ws = useWs();
  const [enabled, setEnabled] = createSignal(false);
  const [richCaptureEnabled, setRichCaptureEnabled] = createSignal(false);
  const [open, setOpen] = createSignal(false);
  const [comment, setComment] = createSignal("");
  const [errorMessage, setErrorMessage] = createSignal("");
  const [category, setCategory] = createSignal<Category>("bug");
  const [severity, setSeverity] = createSignal<Severity>("medium");
  const [includeDom, setIncludeDom] = createSignal(false);
  const [draft, setDraft] = createSignal<BugReportDraftSnapshot | null>(null);
  const [evidenceItems, setEvidenceItems] = createSignal<BugReportEvidenceItem[]>([]);
  const [lastEvidenceAt, setLastEvidenceAt] = createSignal(0);
  const [lastWsEvidence, setLastWsEvidence] = createSignal<Record<string, unknown> | null>(null);
  const [context, setContext] = createSignal<BugReportContext | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [selecting, setSelecting] = createSignal(false);
  const [capturingVisual, setCapturingVisual] = createSignal(false);
  const [dragId, setDragId] = createSignal<string | null>(null);
  const [pointerDragId, setPointerDragId] = createSignal<string | null>(null);
  const [previewVisual, setPreviewVisual] = createSignal<VisualCaptureSnapshot | null>(null);
  const [eventPreview, setEventPreview] = createSignal<EventPreview | null>(null);
  const [fullscreen, setFullscreen] = createSignal(false);
  const [message, setMessage] = createSignal<{ kind: "ok" | "err"; text: string } | null>(null);
  const [activeRecording, setActiveRecording] = createSignal<{ kind: RecordingKind; startedAt: number } | null>(null);
  const [telemetryCarryover, setTelemetryCarryover] = createSignal<EvidenceEventCarryover>(emptyEventCarryover());
  let actionRecorder: BugReportActionRecorder | null = null;
  let storageReady = false;
  let persistTimer: number | null = null;
  let recordingPersistInterval: number | null = null;
  let unloadPersisted = false;

  onMount(() => {
    if (isNativeApp()) return;
    installBugReportCapture();
    const onShortcut = (event: KeyboardEvent) => {
      if (!(event.altKey && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "b")) return;
      event.preventDefault();
      showDialog();
    };
    document.addEventListener("keydown", onShortcut, true);
    const onPageHide = (event: PageTransitionEvent) => persistDraftForUnload(!event.persisted);
    const onBeforeUnload = () => persistDraftForUnload(true);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    storageReady = true;
    restorePersistedDraft();
    onCleanup(() => {
      document.removeEventListener("keydown", onShortcut, true);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (persistTimer !== null) window.clearTimeout(persistTimer);
      if (recordingPersistInterval !== null) window.clearInterval(recordingPersistInterval);
    });
    fetch("/api/bug-reports/config", { credentials: "include", cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((cfg: ConfigResponse | null) => {
        if (!cfg?.ok || !cfg.enabled) return;
        setEnabled(true);
        setRichCaptureEnabled(Boolean(cfg.richCaptureEnabled));
      })
      .catch(() => undefined);
  });

  createEffect(() => {
    comment();
    errorMessage();
    category();
    severity();
    includeDom();
    draft();
    evidenceItems();
    lastEvidenceAt();
    lastWsEvidence();
    open();
    fullscreen();
    const recording = activeRecording();
    telemetryCarryover();
    updateRecordingPersistInterval(recording);
    schedulePersistDraft();
  });

  const captureBusy = createMemo(() => submitting() || selecting() || capturingVisual());
  const evidenceLimitReached = createMemo(() => evidenceItems().length >= MAX_EVIDENCE_ITEMS);
  const canSubmit = createMemo(() => {
    const hasText = comment().trim().length >= 3 || errorMessage().trim().length >= 3;
    return (hasText || evidenceItems().length > 0) && !submitting() && !activeRecording();
  });
  const payloadUsage = createMemo(() => {
    const snapshot = context();
    if (!snapshot) return { bytes: 0, ratio: 0, label: `0 Ko / ${formatBytes(MAX_SUBMIT_PAYLOAD_BYTES)}` };
    const bytes = jsonBytes(JSON.stringify(buildSubmitBody(snapshot)));
    return {
      bytes,
      ratio: Math.min(1, bytes / MAX_SUBMIT_PAYLOAD_BYTES),
      label: `${formatBytes(bytes)} / ${formatBytes(MAX_SUBMIT_PAYLOAD_BYTES)}`,
    };
  });

  function hasDraftContent(): boolean {
    return Boolean(
      draft() || comment().trim() || errorMessage().trim() || evidenceItems().length > 0 || activeRecording(),
    );
  }

  function buildPersistedDraftState(): PersistedBugReportDraftState | null {
    if (!hasDraftContent()) return null;
    const recording = activeRecording();
    return {
      v: 1,
      savedAt: Date.now(),
      open: open(),
      fullscreen: fullscreen(),
      comment: sanitizeText(comment(), 2000),
      errorMessage: sanitizeText(errorMessage(), 2000),
      category: category(),
      severity: severity(),
      includeDom: includeDom(),
      draft: draftWithGlobalText(),
      evidenceItems: renumberEvidenceItems(evidenceItems()).slice(0, MAX_EVIDENCE_ITEMS),
      lastEvidenceAt: lastEvidenceAt(),
      lastWsEvidence: lastWsEvidence(),
      activeRecording: recording,
      recordingEvents: recording?.kind === "telemetry" ? persistedTelemetryEvents(recording.startedAt) : null,
    };
  }

  function writePersistedDraft(): void {
    try {
      const state = buildPersistedDraftState();
      if (!state) {
        clearPersistedDraft();
        return;
      }
      window.sessionStorage.setItem(BUG_REPORT_DRAFT_STORAGE_KEY, serializePersistedDraftState(state));
    } catch {
      // sessionStorage can be disabled or quota-limited; the in-memory draft remains usable.
    }
  }

  function schedulePersistDraft(): void {
    if (!storageReady || typeof window === "undefined") return;
    unloadPersisted = false;
    if (persistTimer !== null) window.clearTimeout(persistTimer);
    persistTimer = window.setTimeout(() => {
      persistTimer = null;
      untrack(() => writePersistedDraft());
    }, 180);
  }

  function persistDraftForUnload(force: boolean): void {
    if (unloadPersisted && !force) return;
    unloadPersisted = true;
    if (persistTimer !== null) {
      window.clearTimeout(persistTimer);
      persistTimer = null;
    }
    writePersistedDraft();
  }

  function clearPersistedDraft(): void {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.removeItem(BUG_REPORT_DRAFT_STORAGE_KEY);
    } catch {
      // ignore unavailable storage
    }
  }

  function restorePersistedDraft(): void {
    const state = readPersistedDraftState();
    if (!state) return;
    setComment(state.comment);
    setErrorMessage(state.errorMessage);
    setCategory(state.category);
    setSeverity(state.severity);
    setIncludeDom(state.includeDom);
    setDraft(state.draft);
    setEvidenceItems(renumberEvidenceItems(state.evidenceItems).slice(0, MAX_EVIDENCE_ITEMS));
    setLastEvidenceAt(state.lastEvidenceAt || Date.now());
    setLastWsEvidence(state.lastWsEvidence || null);
    setFullscreen(state.fullscreen);
    if (state.activeRecording?.kind === "telemetry") {
      setOpen(false);
      setTelemetryCarryover(normalizeCarryover(state.recordingEvents));
      setActiveRecording(state.activeRecording);
      setMessage({ kind: "ok", text: "Brouillon restauré. La capture logs/WS continue après rechargement." });
    } else {
      setOpen(state.open);
      setTelemetryCarryover(emptyEventCarryover());
      setActiveRecording(null);
      if (state.activeRecording?.kind === "action") {
        setMessage({
          kind: "err",
          text: "Brouillon restauré. La capture action a été interrompue par le rechargement.",
        });
        setOpen(true);
      } else {
        setMessage({ kind: "ok", text: "Brouillon de signalement restauré après rechargement." });
      }
    }
    refreshContext(state.includeDom, state.evidenceItems, state.draft);
  }

  function readPersistedDraftState(): PersistedBugReportDraftState | null {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem(BUG_REPORT_DRAFT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<PersistedBugReportDraftState>;
      if (parsed.v !== 1 || typeof parsed.savedAt !== "number") {
        clearPersistedDraft();
        return null;
      }
      if (Date.now() - parsed.savedAt > MAX_PERSISTED_DRAFT_AGE_MS) {
        clearPersistedDraft();
        return null;
      }
      const evidence = Array.isArray(parsed.evidenceItems) ? parsed.evidenceItems : [];
      return {
        v: 1,
        savedAt: parsed.savedAt,
        open: Boolean(parsed.open),
        fullscreen: Boolean(parsed.fullscreen),
        comment: typeof parsed.comment === "string" ? sanitizeText(parsed.comment, 2000) : "",
        errorMessage: typeof parsed.errorMessage === "string" ? sanitizeText(parsed.errorMessage, 2000) : "",
        category: CATEGORIES.includes(parsed.category as Category) ? (parsed.category as Category) : "bug",
        severity: SEVERITIES.includes(parsed.severity as Severity) ? (parsed.severity as Severity) : "medium",
        includeDom: Boolean(parsed.includeDom),
        draft: parsed.draft || null,
        evidenceItems: renumberEvidenceItems(evidence as BugReportEvidenceItem[]).slice(0, MAX_EVIDENCE_ITEMS),
        lastEvidenceAt: typeof parsed.lastEvidenceAt === "number" ? parsed.lastEvidenceAt : 0,
        lastWsEvidence:
          parsed.lastWsEvidence && typeof parsed.lastWsEvidence === "object"
            ? (parsed.lastWsEvidence as Record<string, unknown>)
            : null,
        activeRecording: normalizePersistedRecording(parsed.activeRecording),
        recordingEvents: normalizeCarryover(parsed.recordingEvents),
      };
    } catch {
      clearPersistedDraft();
      return null;
    }
  }

  function wsState(): Record<string, unknown> {
    return ws.state as unknown as Record<string, unknown>;
  }

  function emptyEventCarryover(): EvidenceEventCarryover {
    return { console: [], network: [], wsMessages: [] };
  }

  function normalizeCarryover(value: unknown): EvidenceEventCarryover {
    const input = value && typeof value === "object" ? (value as Partial<EvidenceEventCarryover>) : {};
    return {
      console: Array.isArray(input.console) ? input.console.slice(-MAX_PERSISTED_RECORDING_EVENTS) : [],
      network: Array.isArray(input.network) ? input.network.slice(-MAX_PERSISTED_RECORDING_EVENTS) : [],
      wsMessages: Array.isArray(input.wsMessages) ? input.wsMessages.slice(-MAX_PERSISTED_RECORDING_EVENTS) : [],
    };
  }

  function normalizePersistedRecording(value: unknown): { kind: RecordingKind; startedAt: number } | null {
    if (!value || typeof value !== "object") return null;
    const recording = value as Partial<{ kind: RecordingKind; startedAt: number }>;
    if (recording.kind !== "action" && recording.kind !== "telemetry") return null;
    if (typeof recording.startedAt !== "number" || !Number.isFinite(recording.startedAt)) return null;
    return { kind: recording.kind, startedAt: recording.startedAt };
  }

  function serializePersistedDraftState(state: PersistedBugReportDraftState): string {
    let payload = JSON.stringify(state);
    if (jsonBytes(payload) <= MAX_PERSISTED_DRAFT_BYTES) return payload;
    const compacted: PersistedBugReportDraftState = {
      ...state,
      recordingEvents: state.recordingEvents
        ? {
            console: state.recordingEvents.console.slice(-20),
            network: state.recordingEvents.network.slice(-20),
            wsMessages: state.recordingEvents.wsMessages.slice(-20),
          }
        : null,
      evidenceItems: state.evidenceItems.map((item) => ({
        ...item,
        contextDelta: {
          ...item.contextDelta,
          console: item.contextDelta.console.slice(-10),
          network: item.contextDelta.network.slice(-10),
          wsMessages: item.contextDelta.wsMessages.slice(-10),
        },
      })),
    };
    payload = JSON.stringify(compacted);
    if (jsonBytes(payload) <= MAX_PERSISTED_DRAFT_BYTES) return payload;
    throw new Error("persisted draft too large");
  }

  function mergeEventLists<T extends { t: number }>(previous: T[], current: T[], max: number): T[] {
    const byKey = new Map<string, T>();
    for (const entry of [...previous, ...current]) byKey.set(eventKey(entry), entry);
    return Array.from(byKey.values())
      .sort((a, b) => a.t - b.t)
      .slice(-max);
  }

  function eventKey(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  function persistedTelemetryEvents(startedAt: number): EvidenceEventCarryover {
    const live = getBugReportBuffers();
    return {
      console: mergeEventLists(
        telemetryCarryover().console,
        live.console.filter((entry) => entry.t > startedAt),
        MAX_PERSISTED_RECORDING_EVENTS,
      ),
      network: mergeEventLists(
        telemetryCarryover().network,
        live.network.filter((entry) => entry.t > startedAt),
        MAX_PERSISTED_RECORDING_EVENTS,
      ),
      wsMessages: mergeEventLists(
        telemetryCarryover().wsMessages,
        live.wsMessages.filter((entry) => entry.t > startedAt),
        MAX_PERSISTED_RECORDING_EVENTS,
      ),
    };
  }

  function updateRecordingPersistInterval(recording: { kind: RecordingKind; startedAt: number } | null): void {
    if (typeof window === "undefined") return;
    if (recording?.kind === "telemetry") {
      if (recordingPersistInterval === null) {
        recordingPersistInterval = window.setInterval(() => untrack(() => writePersistedDraft()), 1200);
      }
      return;
    }
    if (recordingPersistInterval !== null) {
      window.clearInterval(recordingPersistInterval);
      recordingPersistInterval = null;
    }
  }

  function buildSubmitBody(snapshot: BugReportContext): SubmitRequestBody {
    return {
      category: category(),
      severity: severity(),
      userComment: sanitizeText(comment(), 2000),
      errorMessage: sanitizeText(errorMessage(), 2000),
      sessionCode: snapshot.sessionCode,
      hostId: snapshot.hostId,
      route: snapshot.route,
      panelId: snapshot.panelId,
      context: snapshot,
    };
  }

  function startDraft(): BugReportDraftSnapshot {
    const existing = draft();
    if (existing) return existing;
    const startedAtMs = Date.now();
    const initial = captureBugReportContext({
      includeDom: false,
      errorMessage: errorMessage(),
      wsState: wsState(),
      evidenceItems: [],
    });
    const next: BugReportDraftSnapshot = {
      draftId: makeId("draft"),
      startedAt: new Date(startedAtMs).toISOString(),
      initialRoute: initial.route,
      initialPanelId: initial.panelId,
      initialContext: {
        route: initial.route,
        panelId: initial.panelId,
        sessionCode: initial.sessionCode,
        hostId: initial.hostId,
        runtime: initial.runtime,
        ws: initial.ws || null,
      },
      globalComment: null,
      globalErrorMessage: null,
    };
    setDraft(next);
    setLastEvidenceAt(startedAtMs);
    setLastWsEvidence((initial.ws || null) as Record<string, unknown> | null);
    return next;
  }

  function draftWithGlobalText(value = draft()): BugReportDraftSnapshot | null {
    if (!value) return null;
    return {
      ...value,
      globalComment: sanitizeText(comment(), 2000) || null,
      globalErrorMessage: sanitizeText(errorMessage(), 2000) || null,
    };
  }

  function refreshContext(
    nextIncludeDom = includeDom(),
    nextEvidence = evidenceItems(),
    nextDraft = draft(),
  ): BugReportContext {
    const snapshot = captureBugReportContext({
      includeDom: nextIncludeDom,
      evidenceItems: renumberEvidenceItems(nextEvidence),
      draft: draftWithGlobalText(nextDraft),
      errorMessage: errorMessage(),
      wsState: wsState(),
    });
    setContext(snapshot);
    return snapshot;
  }

  function showDialog() {
    setMessage(null);
    startDraft();
    clearBugReportElementHighlight();
    refreshContext(includeDom());
    setOpen(true);
  }

  function closeDialog() {
    if (submitting()) return;
    setOpen(false);
    setPreviewVisual(null);
    setEventPreview(null);
    clearBugReportElementHighlight();
  }

  function resetDraft() {
    actionRecorder?.stop();
    actionRecorder = null;
    setActiveRecording(null);
    setComment("");
    setErrorMessage("");
    setIncludeDom(false);
    setDraft(null);
    setEvidenceItems([]);
    setLastEvidenceAt(0);
    setLastWsEvidence(null);
    setTelemetryCarryover(emptyEventCarryover());
    setPreviewVisual(null);
    setEventPreview(null);
    clearBugReportElementHighlight();
    setContext(null);
    clearPersistedDraft();
  }

  async function submit() {
    if (activeRecording()) {
      setMessage({ kind: "err", text: "Arrêtez la capture en cours avant d'envoyer." });
      setOpen(true);
      return;
    }
    const finalDraft = startDraft();
    const finalEvidence = renumberEvidenceItems(evidenceItems());
    setEvidenceItems(finalEvidence);
    const snapshot = captureBugReportContext({
      includeDom: includeDom(),
      evidenceItems: finalEvidence,
      draft: draftWithGlobalText(finalDraft),
      errorMessage: errorMessage(),
      wsState: wsState(),
    });
    setSubmitting(true);
    setMessage(null);
    try {
      const prepared = prepareSubmitPayload(buildSubmitBody(snapshot));
      const res = await fetch("/api/bug-reports", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: prepared.payload,
      });
      const data = (await res.json().catch(() => ({}))) as SubmitResponse;
      if (!res.ok || !data.ok || !data.report) {
        if (res.status === 413) {
          throw new Error(
            "Signalement trop lourd: supprimez une capture visuelle ou réessayez avec une zone plus petite.",
          );
        }
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setMessage({ kind: "ok", text: `Signalement ${data.report.id.slice(0, 8)} créé.` });
      resetDraft();
      refreshContext(false, []);
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  function toggleDom(checked: boolean) {
    setIncludeDom(checked);
    refreshContext(checked);
  }

  function canAddEvidence(): boolean {
    if (captureBusy()) return false;
    if (activeRecording()) {
      setMessage({ kind: "err", text: "Arrêtez la capture en cours avant d'en démarrer une autre." });
      return false;
    }
    if (!evidenceLimitReached()) return true;
    setMessage({ kind: "err", text: `Maximum ${MAX_EVIDENCE_ITEMS} captures par signalement.` });
    return false;
  }

  async function startDomEvidence() {
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    setSelecting(true);
    await nextFrame();
    try {
      const target = await selectBugReportElement();
      if (!target) {
        setMessage({ kind: "err", text: "Sélection annulée." });
        refreshContext(includeDom());
        return;
      }
      addEvidenceItem({ selectedElement: target, visual: null, annotation: null });
      setMessage({ kind: "ok", text: `Capture DOM #${evidenceItems().length} ajoutée.` });
      window.setTimeout(() => highlightBugReportElement(target), 80);
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSelecting(false);
      setOpen(true);
    }
  }

  async function startAnnotatedDomEvidence() {
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    setSelecting(true);
    await nextFrame();
    try {
      const annotations = await selectBugReportDomAnnotations();
      if (!annotations?.length) {
        setMessage({ kind: "err", text: "Capture DOM annotée annulée." });
        refreshContext(includeDom());
        return;
      }
      addEvidenceItem({
        selectedElement: annotations[0]?.selectedElement || null,
        domAnnotations: annotations,
        domSnapshot: captureBugReportDomSnapshot(annotations),
        visual: null,
        annotation: null,
        title: `DOM annoté (${annotations.length} éléments)`,
      });
      setMessage({ kind: "ok", text: `DOM annoté #${evidenceItems().length} ajouté.` });
      window.setTimeout(() => highlightBugReportElement(annotations[0].selectedElement), 80);
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSelecting(false);
      setOpen(true);
    }
  }

  async function startVisualEvidence() {
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    setCapturingVisual(true);
    await nextFrame();
    try {
      const visual = await captureBugReportVisualRegion();
      if (!visual) {
        setMessage({ kind: "err", text: "Capture visuelle annulée." });
        refreshContext(includeDom());
        return;
      }
      addEvidenceItem({ selectedElement: null, visual, annotation: visual.note });
      setMessage({ kind: "ok", text: `Capture visuelle #${evidenceItems().length} ajoutée.` });
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setCapturingVisual(false);
      setOpen(true);
    }
  }

  async function startOneShotEvidence() {
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    setCapturingVisual(true);
    await nextFrame();
    try {
      const visual = await captureBugReportVisualOneShot();
      if (!visual) {
        setMessage({ kind: "err", text: "Capture rapide annulée." });
        refreshContext(includeDom());
        return;
      }
      addEvidenceItem({ selectedElement: null, visual, annotation: null });
      setMessage({ kind: "ok", text: `One shot #${evidenceItems().length} ajouté.` });
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setCapturingVisual(false);
      setOpen(true);
    }
  }

  async function startActionEvidence() {
    if (activeRecording()?.kind === "action") {
      stopActionEvidence();
      return;
    }
    if (activeRecording()) {
      setMessage({ kind: "err", text: "Arrêtez la capture en cours avant d'en démarrer une autre." });
      return;
    }
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    const recorder = startBugReportActionRecording();
    actionRecorder = recorder;
    setActiveRecording({ kind: "action", startedAt: recorder.startedAt });
  }

  function stopActionEvidence() {
    const recording = activeRecording();
    if (recording?.kind !== "action" || !actionRecorder) return;
    const result = actionRecorder.stop();
    actionRecorder = null;
    setActiveRecording(null);
    const primaryAction = result.actions[0] || null;
    addEvidenceItem({
      selectedElement: result.firstTarget,
      visual: null,
      action: primaryAction,
      actions: result.actions,
      annotation: null,
      since: result.startedAt,
      capture: captureWindow("action", result.startedAt, result.stoppedAt),
      title: result.actions.length ? `Actions capturées (${result.actions.length})` : "Capture action sans clic",
    });
    const delta = evidenceItems()[evidenceItems().length - 1]?.contextDelta;
    setMessage({
      kind: "ok",
      text: `Capture action #${evidenceItems().length} ajoutée · clics +${result.actions.length}, logs +${
        delta?.console.length || 0
      }, réseau +${delta?.network.length || 0}, WS +${delta?.wsMessages.length || 0}.`,
    });
    if (result.firstTarget) window.setTimeout(() => highlightBugReportElement(result.firstTarget!), 80);
    setOpen(true);
  }

  function startTelemetryEvidence() {
    if (activeRecording()?.kind === "telemetry") {
      stopTelemetryEvidence();
      return;
    }
    if (activeRecording()) {
      setMessage({ kind: "err", text: "Arrêtez la capture en cours avant d'en démarrer une autre." });
      return;
    }
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    const startedAt = Date.now();
    setTelemetryCarryover(emptyEventCarryover());
    setActiveRecording({ kind: "telemetry", startedAt });
    setOpen(false);
  }

  function stopTelemetryEvidence() {
    const recording = activeRecording();
    if (recording?.kind !== "telemetry") return;
    const stoppedAt = Date.now();
    setActiveRecording(null);
    addEvidenceItem({
      selectedElement: null,
      visual: null,
      action: null,
      actions: [],
      annotation: null,
      since: recording.startedAt,
      capture: captureWindow("telemetry", recording.startedAt, stoppedAt),
      title: "Logs, réseau et WebSocket",
      carryover: telemetryCarryover(),
    });
    setTelemetryCarryover(emptyEventCarryover());
    const delta = evidenceItems()[evidenceItems().length - 1]?.contextDelta;
    setMessage({
      kind: "ok",
      text: `Capture technique #${evidenceItems().length} ajoutée · logs +${delta?.console.length || 0}, réseau +${
        delta?.network.length || 0
      }, WS +${delta?.wsMessages.length || 0}.`,
    });
    setOpen(true);
  }

  async function startFullEvidence() {
    if (!canAddEvidence()) return;
    startDraft();
    setMessage(null);
    setOpen(false);
    setSelecting(true);
    await nextFrame();
    try {
      const target = await selectBugReportElement();
      if (!target) {
        setMessage({ kind: "err", text: "Sélection annulée." });
        refreshContext(includeDom());
        return;
      }
      setSelecting(false);
      let visual: VisualCaptureSnapshot | null = null;
      const wantsVisual = window.confirm("Ajouter une capture de zone à cette étape ?");
      if (wantsVisual) {
        setCapturingVisual(true);
        visual = await captureBugReportVisualRegion();
        setCapturingVisual(false);
      }
      addEvidenceItem({ selectedElement: target, visual, annotation: visual?.note || null });
      setMessage({ kind: "ok", text: `Étape complète #${evidenceItems().length} ajoutée.` });
      window.setTimeout(() => highlightBugReportElement(target), 80);
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSelecting(false);
      setCapturingVisual(false);
      setOpen(true);
    }
  }

  function addEvidenceItem(input: {
    selectedElement: ElementTargetSnapshot | null;
    visual: VisualCaptureSnapshot | null;
    domAnnotations?: DomAnnotationSnapshot[];
    domSnapshot?: DomNodeSnapshot | null;
    action?: BugReportActionSnapshot | null;
    actions?: BugReportActionSnapshot[];
    annotation: string | null;
    since?: number;
    title?: string;
    capture?: BugReportEvidenceCaptureWindow | null;
    carryover?: EvidenceEventCarryover | null;
  }) {
    const now = Date.now();
    const since = input.since ?? lastEvidenceAt();
    let delta = captureBugReportEvidenceDelta({
      since: since > 0 ? since : 0,
      wsState: wsState(),
      previousWs: lastWsEvidence(),
      selectedElement: input.selectedElement,
      visual: input.visual,
      actions: input.actions,
      capture: input.capture || null,
    });
    if (input.carryover) {
      delta = {
        ...delta,
        console: mergeEventLists(input.carryover.console, delta.console, MAX_PERSISTED_RECORDING_EVENTS),
        network: mergeEventLists(input.carryover.network, delta.network, MAX_PERSISTED_RECORDING_EVENTS),
        wsMessages: mergeEventLists(input.carryover.wsMessages, delta.wsMessages, MAX_PERSISTED_RECORDING_EVENTS),
      };
    }
    const current = evidenceItems();
    const item: BugReportEvidenceItem = {
      id: makeId("evidence"),
      index: current.length + 1,
      title: input.title || evidenceTitle(input.selectedElement, input.visual, current.length + 1, input.action),
      annotation: input.annotation ? sanitizeText(input.annotation, 1200).trim() || null : null,
      action: input.action || null,
      selectedElement: input.selectedElement,
      domAnnotations: input.domAnnotations,
      domSnapshot: input.domSnapshot || null,
      visual: input.visual,
      contextDelta: delta,
      createdAt: delta.createdAt,
    };
    const next = renumberEvidenceItems([...current, item]);
    setEvidenceItems(next);
    setLastEvidenceAt(now);
    setLastWsEvidence((delta.ws || null) as Record<string, unknown> | null);
    refreshContext(includeDom(), next);
  }

  function updateEvidenceAnnotation(id: string, value: string) {
    const next = evidenceItems().slice();
    const target = next.find((item) => item.id === id);
    if (!target) return;
    target.annotation = sanitizeText(value, 1200) || null;
    setEvidenceItems(next);
    refreshContext(includeDom(), next);
  }

  function updateDomAnnotationNote(id: string, annotationId: string, value: string) {
    const next = evidenceItems().map((item) => {
      if (item.id !== id || !item.domAnnotations?.length) return item;
      return {
        ...item,
        domAnnotations: item.domAnnotations.map((annotation) =>
          annotation.id === annotationId ? { ...annotation, note: sanitizeText(value, 800) || null } : annotation,
        ),
      };
    });
    setEvidenceItems(next);
    refreshContext(includeDom(), next);
  }

  function removeEvidenceEvent(id: string, kind: EvidenceEventKind, eventIndex: number) {
    const next = evidenceItems().map((item) => {
      if (item.id !== id) return item;
      if (kind === "actions") {
        const actions = item.contextDelta.actions.filter((_, index) => index !== eventIndex);
        return {
          ...item,
          action: actions[0] || null,
          contextDelta: { ...item.contextDelta, actions },
        };
      }
      if (kind === "console") {
        return {
          ...item,
          contextDelta: {
            ...item.contextDelta,
            console: item.contextDelta.console.filter((_, index) => index !== eventIndex),
          },
        };
      }
      if (kind === "network") {
        return {
          ...item,
          contextDelta: {
            ...item.contextDelta,
            network: item.contextDelta.network.filter((_, index) => index !== eventIndex),
          },
        };
      }
      return {
        ...item,
        contextDelta: {
          ...item.contextDelta,
          wsMessages: item.contextDelta.wsMessages.filter((_, index) => index !== eventIndex),
        },
      };
    });
    setEvidenceItems(next);
    refreshContext(includeDom(), next);
  }

  function removeEvidence(id: string) {
    const next = renumberEvidenceItems(evidenceItems().filter((item) => item.id !== id));
    setEvidenceItems(next);
    refreshContext(includeDom(), next);
    setPreviewVisual(null);
  }

  function moveEvidence(id: string, direction: -1 | 1) {
    const current = evidenceItems();
    const from = current.findIndex((item) => item.id === id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= current.length) return;
    const next = current.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    const ordered = renumberEvidenceItems(next);
    setEvidenceItems(ordered);
    refreshContext(includeDom(), ordered);
  }

  function moveEvidenceBefore(sourceId: string | null, targetId: string) {
    if (!sourceId || sourceId === targetId) return;
    const current = evidenceItems();
    const from = current.findIndex((item) => item.id === sourceId);
    const to = current.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0) return;
    const next = current.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    const ordered = renumberEvidenceItems(next);
    setEvidenceItems(ordered);
    refreshContext(includeDom(), ordered);
  }

  function onDragStart(id: string, event: DragEvent) {
    setDragId(id);
    event.dataTransfer?.setData("text/plain", id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
  }

  function onDragOver(id: string, event: DragEvent) {
    if (!dragId() || dragId() === id) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  }

  function onDrop(id: string, event: DragEvent) {
    event.preventDefault();
    moveEvidenceBefore(dragId() || event.dataTransfer?.getData("text/plain") || null, id);
    setDragId(null);
  }

  function startPointerReorder(id: string, event: PointerEvent) {
    event.preventDefault();
    event.stopPropagation();
    setPointerDragId(id);
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  function enterPointerTarget(id: string) {
    moveEvidenceBefore(pointerDragId(), id);
  }

  function stopPointerReorder() {
    setPointerDragId(null);
  }

  function viewEvidence(item: BugReportEvidenceItem) {
    if (item.selectedElement) highlightBugReportElement(item.selectedElement);
    else if (item.domAnnotations?.[0]) highlightBugReportElement(item.domAnnotations[0].selectedElement);
    if (item.visual) setPreviewVisual(item.visual);
  }

  async function editVisualEvidence(id: string) {
    const current = evidenceItems();
    const item = current.find((entry) => entry.id === id);
    if (!item?.visual) return;
    setMessage(null);
    setCapturingVisual(true);
    try {
      const visual = await editBugReportVisualCapture(item.visual);
      if (!visual) {
        setMessage({ kind: "err", text: "Modification de capture annulée." });
        return;
      }
      const next = current.map((entry) => (entry.id === id ? { ...entry, visual } : entry));
      setEvidenceItems(next);
      setPreviewVisual((preview) => (preview === item.visual ? visual : preview));
      refreshContext(includeDom(), next);
      setMessage({ kind: "ok", text: "Annotations visuelles mises à jour." });
    } catch (err) {
      setMessage({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setCapturingVisual(false);
    }
  }

  function previewEvent(title: string, meta: string, body: string) {
    setEventPreview({ title, meta, body });
  }

  function selectedElementLabel(target: ElementTargetSnapshot | null | undefined) {
    if (!target) return "—";
    return target.debugSummary || (target.id ? `${target.tag}#${target.id}` : target.selector);
  }

  function recordingLabel(recording: { kind: RecordingKind; startedAt: number }) {
    return recording.kind === "action" ? "Capture action en cours" : "Capture logs/WS en cours";
  }

  function evidenceEventCount(item: BugReportEvidenceItem) {
    return (
      item.contextDelta.actions.length +
      item.contextDelta.console.length +
      item.contextDelta.network.length +
      item.contextDelta.wsMessages.length
    );
  }

  function evidenceEventSummary(item: BugReportEvidenceItem) {
    const parts: string[] = [];
    if (item.contextDelta.actions.length) parts.push(`actions ${item.contextDelta.actions.length}`);
    if (item.contextDelta.console.length) parts.push(`logs ${item.contextDelta.console.length}`);
    if (item.contextDelta.network.length) parts.push(`réseau ${item.contextDelta.network.length}`);
    if (item.contextDelta.wsMessages.length) parts.push(`WS ${item.contextDelta.wsMessages.length}`);
    return parts.length ? parts.join(" · ") : "0 événement";
  }

  function captureWindowLabel(item: BugReportEvidenceItem) {
    const capture = item.contextDelta.capture;
    if (!capture) return "";
    const label = capture.kind === "action" ? "Action" : "Logs/WS";
    return `${label} · ${formatDuration(capture.durationMs)}`;
  }

  return (
    <Show when={enabled()}>
      <button class="bug-report-fab" type="button" onClick={showDialog} title="Signaler un bug">
        Signaler
      </button>

      <Show when={activeRecording()}>
        {(recording) => (
          <div class="bug-report-recording-bar">
            <div>
              <strong>{recordingLabel(recording())}</strong>
              <span>Démarrée à {new Date(recording().startedAt).toLocaleTimeString()}</span>
            </div>
            <button
              class="btn btn--sm btn--info"
              type="button"
              onClick={() => (recording().kind === "action" ? stopActionEvidence() : stopTelemetryEvidence())}
            >
              Stop et revoir
            </button>
          </div>
        )}
      </Show>

      <Show when={open()}>
        <div
          class="bug-report-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => e.target === e.currentTarget && closeDialog()}
        >
          <div class="bug-report-panel" data-fullscreen={fullscreen() ? "true" : "false"}>
            <header class="bug-report-head">
              <div>
                <div class="bug-report-kicker">Signalement</div>
                <h2>Créer un rapport</h2>
              </div>
              <div class="bug-report-head-actions">
                <div
                  class="bug-report-payload-meter"
                  data-over={payloadUsage().bytes > MAX_SUBMIT_PAYLOAD_BYTES ? "true" : "false"}
                  style={{ "--bug-report-payload-ratio": `${payloadUsage().ratio * 100}%` }}
                >
                  <span>{payloadUsage().label}</span>
                  <i />
                </div>
                <button class="btn btn--sm btn--ghost" type="button" onClick={() => setFullscreen((value) => !value)}>
                  {fullscreen() ? "Réduire" : "Plein écran"}
                </button>
                <button class="btn btn--sm btn--ghost" type="button" onClick={closeDialog}>
                  Fermer
                </button>
                <button class="btn btn--sm btn--info" type="button" disabled={!canSubmit()} onClick={submit}>
                  {submitting() ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </header>

            <div class="bug-report-grid">
              <label class="bug-report-field bug-report-field--wide">
                <span>Annotation globale</span>
                <textarea
                  value={comment()}
                  onInput={(e) => setComment(e.currentTarget.value)}
                  placeholder="Synthèse du problème"
                  rows={3}
                  autofocus
                />
              </label>

              <label class="bug-report-field bug-report-field--wide">
                <span>Message d'erreur / log global</span>
                <textarea
                  value={errorMessage()}
                  onInput={(e) => setErrorMessage(e.currentTarget.value)}
                  placeholder="Erreur affichée, stack, code HTTP, message console"
                  rows={2}
                />
              </label>

              <label class="bug-report-field">
                <span>Catégorie</span>
                <select value={category()} onChange={(e) => setCategory(e.currentTarget.value as Category)}>
                  <option value="bug">Bug</option>
                  <option value="ui">Interface</option>
                  <option value="network">Réseau</option>
                  <option value="performance">Performance</option>
                  <option value="security">Sécurité</option>
                  <option value="other">Autre</option>
                </select>
              </label>

              <label class="bug-report-field">
                <span>Sévérité</span>
                <select value={severity()} onChange={(e) => setSeverity(e.currentTarget.value as Severity)}>
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                  <option value="critical">Critique</option>
                </select>
              </label>
            </div>

            <div class="bug-report-context">
              <div class="bug-report-context-head">
                <span>Captures du signalement</span>
                <div class="bug-report-context-tools">
                  <Show when={richCaptureEnabled()}>
                    <button
                      class="btn btn--xs btn--info"
                      type="button"
                      disabled={captureBusy() || evidenceLimitReached() || Boolean(activeRecording())}
                      onClick={startOneShotEvidence}
                    >
                      {capturingVisual() ? "Capture…" : "One shot"}
                    </button>
                    <button
                      class="btn btn--xs btn--info"
                      type="button"
                      disabled={
                        captureBusy() ||
                        activeRecording()?.kind === "telemetry" ||
                        (!activeRecording() && evidenceLimitReached())
                      }
                      onClick={startActionEvidence}
                    >
                      {activeRecording()?.kind === "action" ? "Stop action" : "Démarrer action"}
                    </button>
                    <button
                      class="btn btn--xs btn--ghost"
                      type="button"
                      disabled={
                        captureBusy() ||
                        activeRecording()?.kind === "action" ||
                        (!activeRecording() && evidenceLimitReached())
                      }
                      onClick={startTelemetryEvidence}
                    >
                      {activeRecording()?.kind === "telemetry" ? "Stop logs/WS" : "Démarrer logs/WS"}
                    </button>
                    <button
                      class="btn btn--xs btn--ghost"
                      type="button"
                      disabled={captureBusy() || evidenceLimitReached() || Boolean(activeRecording())}
                      onClick={startFullEvidence}
                    >
                      {selecting() || capturingVisual() ? "Capture…" : "Ajouter étape complète"}
                    </button>
                    <button
                      class="btn btn--xs btn--ghost"
                      type="button"
                      disabled={captureBusy() || evidenceLimitReached() || Boolean(activeRecording())}
                      onClick={startAnnotatedDomEvidence}
                    >
                      DOM annoté
                    </button>
                    <button
                      class="btn btn--xs btn--ghost"
                      type="button"
                      disabled={captureBusy() || evidenceLimitReached() || Boolean(activeRecording())}
                      onClick={startDomEvidence}
                    >
                      Ajouter capture DOM
                    </button>
                    <button
                      class="btn btn--xs btn--ghost"
                      type="button"
                      disabled={captureBusy() || evidenceLimitReached() || Boolean(activeRecording())}
                      onClick={startVisualEvidence}
                    >
                      Ajouter capture zone
                    </button>
                  </Show>
                  <button class="btn btn--xs btn--ghost" type="button" onClick={() => refreshContext(includeDom())}>
                    Rafraîchir
                  </button>
                </div>
              </div>
              <dl>
                <div>
                  <dt>Route</dt>
                  <dd>{context()?.route || "—"}</dd>
                </div>
                <div>
                  <dt>Panneau</dt>
                  <dd>{context()?.panelId || "—"}</dd>
                </div>
                <div>
                  <dt>Étapes</dt>
                  <dd>
                    {evidenceItems().length}/{MAX_EVIDENCE_ITEMS}
                  </dd>
                </div>
                <div>
                  <dt>Console</dt>
                  <dd>{context()?.console.length ?? 0}</dd>
                </div>
                <div>
                  <dt>Réseau</dt>
                  <dd>{context()?.network.length ?? 0}</dd>
                </div>
                <div>
                  <dt>Erreur</dt>
                  <dd title={errorMessage() || context()?.latestError?.message || ""}>
                    {errorMessage() ? "Manuelle" : context()?.latestError ? "Auto" : "—"}
                  </dd>
                </div>
              </dl>
              <Show when={richCaptureEnabled()}>
                <label class="bug-report-check">
                  <input type="checkbox" checked={includeDom()} onChange={(e) => toggleDom(e.currentTarget.checked)} />
                  <span>Inclure structure DOM globale masquée</span>
                </label>
              </Show>
              <Show when={evidenceLimitReached()}>
                <div class="bug-report-limit">Limite atteinte: supprimez ou réordonnez les captures avant l'envoi.</div>
              </Show>
            </div>

            <div class="bug-report-evidence-list" onPointerUp={stopPointerReorder} onPointerCancel={stopPointerReorder}>
              <Show
                when={evidenceItems().length > 0}
                fallback={<div class="bug-report-evidence-empty">Aucune capture ajoutée à ce brouillon.</div>}
              >
                <For each={evidenceItems()}>
                  {(item, index) => (
                    <article
                      class="bug-report-evidence-item"
                      data-dragging={dragId() === item.id || pointerDragId() === item.id ? "true" : "false"}
                      draggable
                      onDragStart={(event) => onDragStart(item.id, event)}
                      onDragEnd={() => setDragId(null)}
                      onDragOver={(event) => onDragOver(item.id, event)}
                      onDrop={(event) => onDrop(item.id, event)}
                      onPointerEnter={() => enterPointerTarget(item.id)}
                    >
                      <div class="bug-report-evidence-rail">
                        <button
                          class="bug-report-evidence-grip"
                          type="button"
                          title="Glisser pour réordonner"
                          aria-label={`Réordonner capture ${index() + 1}`}
                          onPointerDown={(event) => startPointerReorder(item.id, event)}
                        >
                          ↕
                        </button>
                        <span>#{index() + 1}</span>
                      </div>
                      <div class="bug-report-evidence-body">
                        <div class="bug-report-evidence-top">
                          <strong title={item.title}>{item.title}</strong>
                          <div class="bug-report-evidence-badges">
                            <For each={evidenceBadges(item)}>{(badge) => <span>{badge}</span>}</For>
                          </div>
                        </div>
                        <label class="bug-report-field bug-report-field--compact">
                          <span>Annotation de l'étape</span>
                          <textarea
                            value={item.annotation || ""}
                            onInput={(e) => updateEvidenceAnnotation(item.id, e.currentTarget.value)}
                            placeholder="Ce qu'il faut observer sur cette capture"
                            rows={2}
                          />
                        </label>

                        <Show when={item.action}>
                          {(action) => (
                            <div class="bug-report-action-card">
                              <div class="bug-report-target-copy">
                                <span>Action clic</span>
                                <code title={action().targetSelector}>{action().targetSummary}</code>
                              </div>
                              <div class="bug-report-target-meta">
                                <span>
                                  Point: {action().x},{action().y}
                                </span>
                                <span>Bouton: {action().button}</span>
                                <span>Console +{item.contextDelta.console.length}</span>
                                <span>WS +{item.contextDelta.wsMessages.length}</span>
                              </div>
                            </div>
                          )}
                        </Show>

                        <Show when={item.domAnnotations?.length}>
                          <div class="bug-report-dom-annotations-card">
                            <div class="bug-report-target-copy">
                              <span>DOM annoté</span>
                              <code>{item.domAnnotations?.length || 0} éléments sélectionnés</code>
                            </div>
                            <For each={item.domAnnotations}>
                              {(annotation) => (
                                <div class="bug-report-dom-annotation-row">
                                  <button
                                    class="btn btn--xs btn--ghost"
                                    type="button"
                                    onClick={() => highlightBugReportElement(annotation.selectedElement)}
                                  >
                                    #{annotation.index}
                                  </button>
                                  <div>
                                    <strong title={annotation.selectedElement.selector}>{annotation.title}</strong>
                                    <textarea
                                      value={annotation.note || ""}
                                      onInput={(e) =>
                                        updateDomAnnotationNote(item.id, annotation.id, e.currentTarget.value)
                                      }
                                      placeholder="Note pour le LLM sur cet élément DOM"
                                      rows={2}
                                    />
                                  </div>
                                </div>
                              )}
                            </For>
                            <div class="bug-report-target-meta">
                              <span>{item.domSnapshot ? "DOM global capturé" : "DOM global absent"}</span>
                            </div>
                          </div>
                        </Show>

                        <Show when={item.selectedElement}>
                          {(target) => (
                            <div class="bug-report-target-card bug-report-target-card--compact">
                              <div class="bug-report-target-copy">
                                <span>DOM ciblé</span>
                                <code title={target().selector}>{selectedElementLabel(target())}</code>
                              </div>
                              <div class="bug-report-target-meta">
                                <span>Panneau: {target().panel || "—"}</span>
                                <span>Composant: {target().component?.name || "—"}</span>
                                <span>{target().dom ? "Sous-arbre capturé" : "Sous-arbre masqué"}</span>
                              </div>
                              <Show when={target().table}>
                                {(table) => (
                                  <div class="bug-report-target-table">
                                    <span>
                                      Table: ligne {table().rowIndex}, colonne{" "}
                                      {table().columnHeader || table().columnIndex}
                                    </span>
                                    <code title={table().rowText || ""}>{table().rowText}</code>
                                  </div>
                                )}
                              </Show>
                            </div>
                          )}
                        </Show>

                        <Show when={item.visual}>
                          {(visual) => (
                            <div class="bug-report-visual-card bug-report-visual-card--compact">
                              <div class="bug-report-target-copy">
                                <span>{visual().kind === "one-shot" ? "Capture rapide" : "Capture annotée"}</span>
                                <code title={visual().note || ""}>
                                  {visual().note || `${visual().bounds.w}×${visual().bounds.h}px`}
                                </code>
                              </div>
                              <div
                                class="bug-report-visual-frame-wrap"
                                style={{ "aspect-ratio": `${visual().image.width} / ${visual().image.height}` }}
                              >
                                <img src={visual().image.dataUrl} alt={`Capture annotée #${index() + 1}`} />
                                <For each={visual().markups || []}>
                                  {(markup) => (
                                    <div class="bug-report-visual-frame" style={visualMarkupStyle(visual(), markup)}>
                                      <span>{markup.title}</span>
                                    </div>
                                  )}
                                </For>
                              </div>
                              <div class="bug-report-target-meta">
                                <span>
                                  Zone: {visual().bounds.w}×{visual().bounds.h}
                                </span>
                                <span>Image: {Math.round(visual().image.bytesApprox / 1024)} Ko</span>
                                <span>Cadres: {visual().markups?.length || 0}</span>
                                <span>Composant: {visual().component?.name || visual().panel || "—"}</span>
                              </div>
                              <Show when={visual().markups?.length}>
                                <div class="bug-report-visual-markups">
                                  <For each={visual().markups}>
                                    {(markup) => (
                                      <div>
                                        <span style={{ background: markup.color }} />
                                        <strong>{markup.title}</strong>
                                        <em>{markup.note || "—"}</em>
                                      </div>
                                    )}
                                  </For>
                                </div>
                              </Show>
                              <div class="bug-report-visual-tools">
                                <button
                                  class="btn btn--xs btn--ghost"
                                  type="button"
                                  onClick={() => editVisualEvidence(item.id)}
                                >
                                  Rééditer cadres
                                </button>
                              </div>
                            </div>
                          )}
                        </Show>

                        <Show when={evidenceEventCount(item) > 0 || item.contextDelta.capture}>
                          <details class="bug-report-event-viewer" open={Boolean(item.contextDelta.capture)}>
                            <summary>
                              <span>Événements capturés</span>
                              <em>{evidenceEventSummary(item)}</em>
                            </summary>
                            <Show when={item.contextDelta.capture}>
                              <div class="bug-report-event-window">{captureWindowLabel(item)}</div>
                            </Show>
                            <Show when={item.contextDelta.actions.length}>
                              <div class="bug-report-event-group">
                                <div class="bug-report-event-group-title">Actions</div>
                                <For each={item.contextDelta.actions}>
                                  {(action, eventIndex) => (
                                    <div class="bug-report-event-row">
                                      <div class="bug-report-event-main">
                                        <strong>{formatActionEvent(action)}</strong>
                                        <span title={action.targetSelector}>{action.targetSummary}</span>
                                      </div>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() =>
                                          previewEvent(formatActionEvent(action), "Action", formatEventDetails(action))
                                        }
                                      >
                                        Voir
                                      </button>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() => removeEvidenceEvent(item.id, "actions", eventIndex())}
                                      >
                                        Retirer
                                      </button>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </Show>
                            <Show when={item.contextDelta.console.length}>
                              <div class="bug-report-event-group">
                                <div class="bug-report-event-group-title">Console</div>
                                <For each={item.contextDelta.console}>
                                  {(entry, eventIndex) => (
                                    <div class="bug-report-event-row">
                                      <div class="bug-report-event-main">
                                        <strong>{formatConsoleEvent(entry)}</strong>
                                        <span>{entry.message}</span>
                                      </div>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() =>
                                          previewEvent(formatConsoleEvent(entry), "Console", formatEventDetails(entry))
                                        }
                                      >
                                        Voir
                                      </button>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() => removeEvidenceEvent(item.id, "console", eventIndex())}
                                      >
                                        Retirer
                                      </button>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </Show>
                            <Show when={item.contextDelta.network.length}>
                              <div class="bug-report-event-group">
                                <div class="bug-report-event-group-title">Réseau</div>
                                <For each={item.contextDelta.network}>
                                  {(entry, eventIndex) => (
                                    <div class="bug-report-event-row">
                                      <div class="bug-report-event-main">
                                        <strong>{formatNetworkEvent(entry)}</strong>
                                        <span title={entry.url}>{entry.url}</span>
                                      </div>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() =>
                                          previewEvent(formatNetworkEvent(entry), "Réseau", formatEventDetails(entry))
                                        }
                                      >
                                        Voir
                                      </button>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() => removeEvidenceEvent(item.id, "network", eventIndex())}
                                      >
                                        Retirer
                                      </button>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </Show>
                            <Show when={item.contextDelta.wsMessages.length}>
                              <div class="bug-report-event-group">
                                <div class="bug-report-event-group-title">WebSocket</div>
                                <For each={item.contextDelta.wsMessages}>
                                  {(entry, eventIndex) => (
                                    <div class="bug-report-event-row">
                                      <div class="bug-report-event-main">
                                        <strong>{formatWsEvent(entry)}</strong>
                                        <span>{entry.message}</span>
                                      </div>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() =>
                                          previewEvent(formatWsEvent(entry), "WebSocket", formatEventDetails(entry))
                                        }
                                      >
                                        Voir
                                      </button>
                                      <button
                                        class="btn btn--xs btn--ghost"
                                        type="button"
                                        onClick={() => removeEvidenceEvent(item.id, "wsMessages", eventIndex())}
                                      >
                                        Retirer
                                      </button>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </Show>
                          </details>
                        </Show>

                        <div class="bug-report-target-actions">
                          <button class="btn btn--xs btn--ghost" type="button" onClick={() => viewEvidence(item)}>
                            Voir
                          </button>
                          <button
                            class="btn btn--xs btn--ghost"
                            type="button"
                            disabled={index() === 0}
                            onClick={() => moveEvidence(item.id, -1)}
                            aria-label={`Monter capture ${index() + 1}`}
                          >
                            ↑
                          </button>
                          <button
                            class="btn btn--xs btn--ghost"
                            type="button"
                            disabled={index() === evidenceItems().length - 1}
                            onClick={() => moveEvidence(item.id, 1)}
                            aria-label={`Descendre capture ${index() + 1}`}
                          >
                            ↓
                          </button>
                          <button class="btn btn--xs btn--ghost" type="button" onClick={() => removeEvidence(item.id)}>
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </article>
                  )}
                </For>
              </Show>
            </div>

            <Show when={previewVisual()}>
              {(visual) => (
                <div class="bug-report-preview" role="dialog" aria-label="Prévisualisation capture">
                  <button type="button" onClick={() => setPreviewVisual(null)} aria-label="Fermer prévisualisation">
                    ×
                  </button>
                  <div
                    class="bug-report-preview-frame-wrap"
                    style={{ "aspect-ratio": `${visual().image.width} / ${visual().image.height}` }}
                  >
                    <img src={visual().image.dataUrl} alt="Prévisualisation de la capture annotée" />
                    <For each={visual().markups || []}>
                      {(markup) => (
                        <div class="bug-report-visual-frame" style={visualMarkupStyle(visual(), markup)}>
                          <span>{markup.title}</span>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </Show>

            <Show when={eventPreview()}>
              {(event) => (
                <div class="bug-report-event-preview" role="dialog" aria-label="Détail événement capturé">
                  <div class="bug-report-event-preview-panel">
                    <header>
                      <div>
                        <span>{event().meta}</span>
                        <strong>{event().title}</strong>
                      </div>
                      <button type="button" onClick={() => setEventPreview(null)} aria-label="Fermer détail événement">
                        ×
                      </button>
                    </header>
                    <pre>{event().body}</pre>
                  </div>
                </div>
              )}
            </Show>

            <Show when={message()}>
              {(m) => (
                <div class="bug-report-message" data-kind={m().kind}>
                  {m().text}
                </div>
              )}
            </Show>

            <footer class="bug-report-actions">
              <span>{canSubmit() ? "Prêt à envoyer" : "Ajoutez une annotation ou une capture"}</span>
            </footer>
          </div>
        </div>
      </Show>
    </Show>
  );
}

function renumberEvidenceItems(items: BugReportEvidenceItem[]): BugReportEvidenceItem[] {
  return items.map((item, index) => (item.index === index + 1 ? item : { ...item, index: index + 1 }));
}

function captureWindow(kind: RecordingKind, startedAt: number, stoppedAt: number): BugReportEvidenceCaptureWindow {
  return {
    kind,
    startedAt: new Date(startedAt).toISOString(),
    stoppedAt: new Date(stoppedAt).toISOString(),
    durationMs: Math.max(0, stoppedAt - startedAt),
  };
}

function evidenceTitle(
  target: ElementTargetSnapshot | null,
  visual: VisualCaptureSnapshot | null,
  index: number,
  action?: BugReportActionSnapshot | null,
): string {
  if (action) return `Action clic: ${action.targetSummary}`;
  if (target?.debugSummary) return target.debugSummary;
  if (visual?.kind === "one-shot") return `One shot ${visual.bounds.w}×${visual.bounds.h}`;
  if (visual?.note) return sanitizeText(visual.note, 180);
  if (visual) return `Capture zone ${visual.bounds.w}×${visual.bounds.h}`;
  return `Capture ${index}`;
}

function evidenceBadges(item: BugReportEvidenceItem): string[] {
  const badges: string[] = [];
  const actionCount = item.contextDelta.actions.length || (item.action ? 1 : 0);
  if (actionCount > 1) badges.push(`Actions +${actionCount}`);
  else if (actionCount === 1) badges.push("Action");
  if (item.domAnnotations?.length) badges.push(`DOM x${item.domAnnotations.length}`);
  else if (item.selectedElement) badges.push("DOM");
  if (item.visual?.kind === "one-shot") badges.push("One shot");
  else if (item.visual) badges.push("Visuel");
  if (item.contextDelta.console.length) badges.push(`Logs +${item.contextDelta.console.length}`);
  if (item.contextDelta.network.length) badges.push(`Réseau +${item.contextDelta.network.length}`);
  if (item.contextDelta.wsMessages.length) badges.push(`WS +${item.contextDelta.wsMessages.length}`);
  else if (item.contextDelta.wsChanged || item.contextDelta.ws) badges.push("WS");
  return badges;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${Math.round(ms / 100) / 10}s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} Mo`;
}

function formatActionEvent(action: BugReportActionSnapshot): string {
  return `Clic ${action.x},${action.y}`;
}

function formatConsoleEvent(entry: EvidenceConsoleEvent): string {
  return entry.level.toUpperCase();
}

function formatNetworkEvent(entry: EvidenceNetworkEvent): string {
  return `${entry.method || "GET"} ${entry.status || "ERR"}`;
}

function formatWsEvent(entry: EvidenceWsEvent): string {
  const op = entry.op || entry.type || entry.family || entry.kind;
  return `${entry.direction.toUpperCase()} ${op}`;
}

function formatEventDetails(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function visualMarkupStyle(
  visual: VisualCaptureSnapshot,
  markup: NonNullable<VisualCaptureSnapshot["markups"]>[number],
): Record<string, string> {
  const width = Math.max(1, visual.image.width);
  const height = Math.max(1, visual.image.height);
  return {
    "--bug-report-markup-color": markup.color,
    left: `${(markup.bounds.x / width) * 100}%`,
    top: `${(markup.bounds.y / height) * 100}%`,
    width: `${(markup.bounds.w / width) * 100}%`,
    height: `${(markup.bounds.h / height) * 100}%`,
  };
}

function makeId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function prepareSubmitPayload(body: SubmitRequestBody): { payload: string; compacted: boolean; bytes: number } {
  let payload = JSON.stringify(body);
  let bytes = jsonBytes(payload);
  if (bytes <= MAX_SUBMIT_PAYLOAD_BYTES) return { payload, compacted: false, bytes };

  for (const level of [1, 2]) {
    payload = JSON.stringify({ ...body, context: compactContextForSubmit(body.context, level) });
    bytes = jsonBytes(payload);
    if (bytes <= MAX_SUBMIT_PAYLOAD_BYTES) return { payload, compacted: true, bytes };
  }

  throw new Error("Signalement trop lourd: supprimez une capture visuelle ou réessayez avec une zone plus petite.");
}

function compactContextForSubmit(context: BugReportContext, level: number): BugReportContext {
  const evidenceItems = context.evidenceItems?.map((item) => compactEvidenceItemForSubmit(item, level));
  return {
    ...context,
    console: context.console.slice(-COMPACT_GLOBAL_EVENTS),
    network: context.network.slice(-COMPACT_GLOBAL_EVENTS),
    ...(level >= 1 ? { dom: null } : {}),
    ...(level >= 2 ? { ws: undefined } : {}),
    evidenceItems,
    evidenceSummary: undefined,
  };
}

function compactEvidenceItemForSubmit(item: BugReportEvidenceItem, level: number): BugReportEvidenceItem {
  return {
    ...item,
    selectedElement: level >= 2 ? compactSelectedElement(item.selectedElement) : item.selectedElement,
    domAnnotations: item.domAnnotations?.map((annotation) => ({
      ...annotation,
      selectedElement: level >= 2 ? compactSelectedElement(annotation.selectedElement)! : annotation.selectedElement,
    })),
    domSnapshot: level >= 2 ? null : item.domSnapshot,
    contextDelta: {
      ...item.contextDelta,
      console: item.contextDelta.console.slice(-COMPACT_ITEM_EVENTS),
      network: item.contextDelta.network.slice(-COMPACT_ITEM_EVENTS),
      wsMessages: item.contextDelta.wsMessages.slice(-COMPACT_ITEM_EVENTS),
      ...(level >= 2 && !item.contextDelta.wsChanged ? { ws: null } : {}),
    },
  };
}

function compactSelectedElement(target: ElementTargetSnapshot | null | undefined): ElementTargetSnapshot | null {
  if (!target) return null;
  return {
    ...target,
    ancestry: target.ancestry.slice(-6),
    dom: null,
  };
}

function jsonBytes(value: string): number {
  return new TextEncoder().encode(value).length;
}
